<template>
  <f7-page-content ref="scrollElementRef"
    ptr
    ptr-mousewheel
    hide-navbar-on-scroll
    :style="contentPaddingStyle"
    @ptr:refresh="handlePullToRefresh"
  >
    <!-- Measured (ResizeObserver): its height is part of where the list
         starts, so a header that appears, grows or goes away after first
         paint — the تدبيسة strip, the pending-review banner — moves the
         virtualizer's scrollMargin with it. flow-root keeps the slot's own
         margins inside the box being measured. -->
    <div ref="headerRef" class="virtual-post-list__header">
      <slot name="header" />
    </div>

    <!-- Loading State -->
    <f7-block v-if="loading && !items.length" class="text-align-center loading-block">
      <f7-preloader></f7-preloader>
      <p class="margin-top">{{ loadingText }}</p>
    </f7-block>

    <!-- Error State -->
    <f7-block v-else-if="error" class="text-align-center error-block">
      <f7-icon f7="exclamationmark_triangle" size="48px" color="red"></f7-icon>
      <h3>حدث خطأ في التحميل</h3>
      <p>{{ error }}</p>
      <f7-button fill color="blue" @click="$emit('retry')">إعادة المحاولة</f7-button>
    </f7-block>

    <!-- Virtual List Container. A row's `start` counts from the top of the
         scroll content (it includes scrollMargin); this box itself starts at
         scrollMargin, so each row sits at start − scrollMargin inside it
         (tanstack's documented form for a list below other content). -->
    <div v-else-if="hasItems && fready" class="virtual-post-list__body">
      <div
        :style="{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }"
      >
        <div
          v-for="virtualRow in virtualItems"
          :key="items[virtualRow.index].id"
          :ref="(el) => measureElement(el, virtualRow.index)"
          :data-index="virtualRow.index"
          class="virtual-item-wrapper"
          :style="{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualRow.start - scrollMargin}px)`,
          }"
        >
          <slot name="item" :item="items[virtualRow.index]" :index="virtualRow.index">
            <post-card
              :post-id="items[virtualRow.index].id"
              :current-feed="currentFeed"
            ></post-card>
          </slot>
        </div>
      </div>

      <!-- Loading More Indicator -->
      <div v-if="isLoadingMore && hasMore" class="skeleton-loading-container">
        <slot name="loading">
          <skeleton-card v-for="n in 2" :key="`skeleton-${n}`"></skeleton-card>
        </slot>
      </div>
    </div>

    <!-- Empty State -->
    <f7-block v-else class="text-align-center empty-state-block">
      <slot name="empty" :clear-filters="() => $emit('clear-filters')">
        <div class="empty-icon-container">
          <f7-icon :f7="emptyIcon" size="64px" color="gray"></f7-icon>
        </div>
        <h3 class="margin-vertical">{{ emptyTitle }}</h3>
        <p class="text-color-gray margin-bottom">{{ emptyDescription }}</p>
        <f7-button
          v-if="hasActiveFilters"
          fill
          color="blue"
          round
          @click="$emit('clear-filters')"
        >
          <f7-icon f7="arrow_counterclockwise" size="16px"></f7-icon>
          مسح الفلاتر
        </f7-button>
      </slot>
    </f7-block>
  </f7-page-content>
</template>

<script setup>
import { useVirtualizer } from '@tanstack/vue-virtual';
import { f7, f7ready } from 'framework7-vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import PostCard from '@/components/posts/PostCard.vue';
import SkeletonCard from '@/components/posts/SkeletonCard.vue';
import { cssLengthToPx } from '@/utils/cssLength';

// =============================================================================
// PROPS & EMITS
// =============================================================================

const props = defineProps({
  items: {
    type: Array,
    required: true,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: [String, null],
    default: null,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
  hasMore: {
    type: Boolean,
    default: true,
  },
  currentFeed: {
    type: String,
    default: 'home',
  },
  hasActiveFilters: {
    type: Boolean,
    default: false,
  },
  emptyIcon: {
    type: String,
    default: 'magnifyingglass',
  },
  emptyTitle: {
    type: String,
    default: 'لا توجد نتائج',
  },
  emptyDescription: {
    type: String,
    default: 'جرب تغيير معايير البحث أو الفلاتر',
  },
  // First-load spinner caption. Overridable so non-post feeds (e.g. the
  // supplier wall) can localise it; defaults to the posts copy.
  loadingText: {
    type: String,
    default: 'جاري تحميل المنشورات...',
  },
  // CSS selector of the navbar this list auto-hides on forward scroll. Defaults
  // to the home navbar so HomePage is unchanged; the supplier wall passes its
  // own so scrolling there never hides the (background) home navbar.
  navbarSelector: {
    type: String,
    default: '.home-navbar',
  },
  estimatedItemSize: {
    type: Number,
    default: 650,
  },
  overscan: {
    type: Number,
    default: 2,
  },
  // Optional explicit bottom padding for the scroll content (a CSS length
  // string). Needed ONLY when this page-content lives in an
  // `<f7-page :page-content="false">` where a bottom toolbar is NOT a preceding
  // DOM sibling of the page-content — then F7's `.toolbar-bottom ~ *` rule never
  // fires and padding-bottom stays 0, clipping the last item behind the bar
  // (e.g. SupplierWallPostDetailPage's batch-claim bar, rendered AFTER this
  // list). Applied additively via F7's first-class
  // `--f7-page-content-extra-padding-bottom` var so it never clobbers F7's own
  // offset. Leave null (default) for tab feeds (HomePage, the supplier wall) —
  // there the tabbar offset already inherits in and a value here would
  // double-pad.
  contentPaddingBottom: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['load-more', 'refresh', 'retry', 'clear-filters']);

// =============================================================================
// STATE
// =============================================================================

const scrollElementRef = ref(null);
const headerRef = ref(null);
const lastLoadMoreIndex = ref(-1);
const isLoadMorePending = ref(false);
let loadMoreTimeout = null;
const fready = ref(false);
// True only between onMounted and onBeforeUnmount. Gates the virtualizer's
// onChange navbar driving so a pending tanstack measurement can't call
// f7.navbar.hide/show during teardown (a detached navbar throws). The primary
// unmount race is fixed by `unloadTabContent: false` in App.vue; this guards the
// secondary teardown crash class on full shell-exit unmounts.
const isMounted = ref(false);

// =============================================================================
// COMPUTED
// =============================================================================

const hasItems = computed(() => props.items.length > 0);

// Additive bottom padding (see `contentPaddingBottom` prop). Null → no inline
// style, so F7's own toolbar/tabbar offset is left untouched.
const contentPaddingStyle = computed(() =>
  props.contentPaddingBottom
    ? { '--f7-page-content-extra-padding-bottom': props.contentPaddingBottom }
    : null,
);

// =============================================================================
// TANSTACK VIRTUAL CONFIGURATION
// =============================================================================

// ---- Where the list starts (scrollMargin) ----------------------------------
//
// The virtualizer must know where its first row sits inside the scroll
// content: the page-content's top padding (navbar + subnavbar + safe area, as
// F7 lays it out), the header slot, and the gap above the first row. It used
// to be `offsetTop + --f7-navbar-height + --f7-safe-area-top`, read once:
// `offsetTop` was whatever it was mid page-transition (WebKit read ~290px, so
// the first post sat far down the screen), the subnavbar was missing, and a
// header that arrived after first paint was never counted.
//
// The padding is read off the element's computed style — a resolved pixel
// length even while impersonating, when `--f7-safe-area-top` is a calc()
// string (cssLengthToPx, never parseInt; see src/utils/cssLength.js).
const LIST_GAP_PX = 12; // .virtual-post-list__body margin-top
const contentPaddingTop = ref(0);
const headerHeight = ref(0);
const scrollMargin = computed(() => contentPaddingTop.value + headerHeight.value + LIST_GAP_PX);

function scrollEl() {
  return scrollElementRef.value?.$el ?? null;
}

function readContentPaddingTop() {
  const el = scrollEl();
  if (!el || typeof window === 'undefined') return;
  contentPaddingTop.value = cssLengthToPx(window.getComputedStyle(el).paddingTop);
}

/**
 * The header slot changed height. If she has already scrolled past it, the
 * change happens above what she is reading — move the scroll position by the
 * same amount so nothing she is looking at moves. At the top (the header in
 * view, or a pull to refresh) it simply pushes the feed down, as a first
 * paint would.
 */
// Until when a scroll is our own correction, not her finger — the navbar
// hide/show in onChange must not read it as "she scrolled down".
let ownScrollUntil = 0;

function onHeaderResize() {
  const el = headerRef.value;
  if (!el) return;
  const scroller = scrollEl();
  // A list with no layout — an inactive tab, a v-show'd panel — reports 0.
  // Keep the last real height: recording 0 would turn the next reveal into a
  // "header grew" and push her down by it every time she comes back.
  if (scroller && scroller.getClientRects().length === 0) return;

  const previous = headerHeight.value;
  const next = el.offsetHeight;
  if (next === previous) return;

  if (scroller && scroller.scrollTop > previous) {
    ownScrollUntil = performance.now() + 150;
    scroller.scrollTop += next - previous;
  }
  headerHeight.value = next;
}

/** The page-content itself resized: F7 changed its padding (a top toolbar
 *  such as the active-filters bar, a safe-area change) or the viewport moved. */
function onScrollerResize() {
  readContentPaddingTop();
}

let headerObserver = null;
let scrollerObserver = null;

function observeLayout() {
  if (typeof ResizeObserver === 'undefined') return;
  if (!headerObserver && headerRef.value) {
    headerObserver = new ResizeObserver(onHeaderResize);
    headerObserver.observe(headerRef.value);
  }
  // `.page-content` is height:100% + border-box: a padding change resizes its
  // content box, which is what a ResizeObserver reports.
  if (!scrollerObserver && scrollEl()) {
    scrollerObserver = new ResizeObserver(onScrollerResize);
    scrollerObserver.observe(scrollEl());
  }
}

const virtualizerOptions = computed(() => ({
  count: props.items.length,
  getScrollElement: () => scrollElementRef.value?.$el,
  estimateSize: () => props.estimatedItemSize,
  overscan: props.overscan,
  // CRITICAL FIX: Disable debug in production for performance
  debug: false,

  gap: 2,
  onChange: (instance) => {
    // Skip while unmounting/unmounted — a pending measurement can still fire
    // onChange during teardown, and f7.navbar.hide/show on a detached navbar throws.
    if (!isMounted.value) return;
    if (performance.now() < ownScrollUntil) return;
    if (instance.scrollDirection === 'forward') {
      f7.navbar.hide(props.navbarSelector, true, true);
    } else if (instance.scrollDirection === 'backward') {
      f7.navbar.show(props.navbarSelector, true);
    }
  },
  // CRITICAL FIX: Better measurement function
  measureElement:
      typeof window !== 'undefined' && 'ResizeObserver' in window
        ? (element) => {
          if (!element) return props.estimatedItemSize;
          const height = element?.getBoundingClientRect().height;
          return height || props.estimatedItemSize;
        }
        : undefined,
  // Where the first row sits in the scroll content — see "Where the list
  // starts" above. Always finite: a NaN here collapses the visible range to a
  // single post that cannot scroll (the act-as bug, src/utils/cssLength.js).
  scrollMargin: scrollMargin.value,
  // Enable lane for single column layout
  lanes: 1,
}));

const virtualizer = useVirtualizer(virtualizerOptions);
const virtualItems = computed(() => virtualizer.value.getVirtualItems());

// =============================================================================
// MEASUREMENT
// =============================================================================

function measureElement(el, _index) {
  if (!el) return el;

  // CRITICAL FIX: Only measure if virtualizer is available and element is valid
  if (virtualizer.value?.measureElement && el instanceof HTMLElement) {
    try {
      virtualizer.value.measureElement(el);
    } catch (error) {
      console.warn('[VirtualPostList] Measurement error:', error);
    }
  }

  return el;
}

// =============================================================================
// INFINITE SCROLL WITH DEBOUNCE
// =============================================================================

// CRITICAL FIX: Debounced load more function
function debouncedLoadMore() {
  // Clear existing timeout
  if (loadMoreTimeout) {
    clearTimeout(loadMoreTimeout);
  }

  // Set new timeout
  loadMoreTimeout = setTimeout(() => {
    if (!isLoadMorePending.value && props.hasMore && !props.isLoadingMore && !props.loading) {
      isLoadMorePending.value = true;
      console.log('[VirtualPostList] Triggering load more');
      emit('load-more');

      // Reset pending flag after a delay
      setTimeout(() => {
        isLoadMorePending.value = false;
      }, 1000);
    }
  }, 300); // 300ms debounce
}

// CRITICAL FIX: Watch with better guards and debouncing
watch(virtualItems, (items) => {
  // Early returns for all blocking conditions
  if (!items || items.length === 0) return;
  if (!props.hasMore) return;
  if (props.isLoadingMore || props.loading) return;
  if (isLoadMorePending.value) return;

  const lastItem = items[items.length - 1];
  if (!lastItem) return;

  // Check if we're at the end
  const isAtEnd = lastItem.index >= props.items.length - 1;
  const isNewIndex = lastItem.index !== lastLoadMoreIndex.value;

  if (isAtEnd && isNewIndex) {
    console.log('[VirtualPostList] Reached end, scheduling load more');
    lastLoadMoreIndex.value = lastItem.index;
    debouncedLoadMore();
  }
}, { flush: 'post' }); // CRITICAL: Use 'post' flush to run after DOM updates

// CRITICAL FIX: Reset tracker when items change significantly
watch(
  () => props.items.length,
  (newLength, oldLength) => {
    // Reset on refresh (length becomes smaller) or initial load
    if (oldLength === 0 || newLength < oldLength) {
      console.log('[VirtualPostList] Resetting load more tracker');
      lastLoadMoreIndex.value = -1;
      isLoadMorePending.value = false;
    }
  },
);

// CRITICAL FIX: Reset on feed change
watch(
  () => props.currentFeed,
  () => {
    console.log('[VirtualPostList] Feed changed, resetting');
    lastLoadMoreIndex.value = -1;
    isLoadMorePending.value = false;

    // Clear any pending load more
    if (loadMoreTimeout) {
      clearTimeout(loadMoreTimeout);
      loadMoreTimeout = null;
    }
  },
);

// =============================================================================
// EVENT HANDLERS
// =============================================================================

async function handlePullToRefresh(done) {
  try {
    console.log('[VirtualPostList] Pull to refresh triggered');

    // Reset states
    lastLoadMoreIndex.value = -1;
    isLoadMorePending.value = false;

    // Clear any pending load more
    if (loadMoreTimeout) {
      clearTimeout(loadMoreTimeout);
      loadMoreTimeout = null;
    }

    emit('refresh', done);
  } catch (err) {
    console.error('[VirtualPostList] Pull-to-refresh error:', err);
    done();
  }
}

// =============================================================================
// PUBLIC API
// =============================================================================

function scrollToIndex(index, options = {}) {
  if (virtualizer.value?.scrollToIndex) {
    virtualizer.value.scrollToIndex(index, {
      align: 'start',
      behavior: 'smooth',
      ...options,
    });
  }
}

function scrollToTop(smooth = true) {
  if (virtualizer.value?.scrollToOffset) {
    virtualizer.value.scrollToOffset(0, {
      behavior: smooth ? 'smooth' : 'auto',
    });
  }
}

function getScrollOffset() {
  return virtualizer.value?.scrollOffset ?? 0;
}

function getVisibleRange() {
  const items = virtualizer.value?.getVirtualItems() ?? [];
  if (items.length === 0) return { start: 0, end: 0 };

  return {
    start: items[0].index,
    end: items[items.length - 1].index,
  };
}

defineExpose({
  scrollToIndex,
  scrollToTop,
  getScrollOffset,
  getVisibleRange,
  virtualizer,
});

onMounted(() => {
  isMounted.value = true;
  headerHeight.value = headerRef.value?.offsetHeight ?? 0;
  readContentPaddingTop();
  observeLayout();
  // Wait for Framework7 page-content to be fully ready
  f7ready(async (_f7) => {
    await nextTick();
    // F7 has laid the page out by now (navbar, subnavbar, safe area).
    readContentPaddingTop();
    fready.value = true;
  });
});

// =============================================================================
// CLEANUP
// =============================================================================

onBeforeUnmount(() => {
  isMounted.value = false;
  headerObserver?.disconnect();
  headerObserver = null;
  scrollerObserver?.disconnect();
  scrollerObserver = null;

  // Clear any pending timeouts
  if (loadMoreTimeout) {
    clearTimeout(loadMoreTimeout);
    loadMoreTimeout = null;
  }

  // Reset states
  lastLoadMoreIndex.value = -1;
  isLoadMorePending.value = false;
});
</script>

<style scoped>
/* The list keeps her place itself when the header above it changes height
   (onHeaderResize). Chrome's own scroll anchoring would move her a second
   time; Safari has none — so it is off, and every browser behaves alike. */
.page-content {
  overflow-anchor: none;
}

.virtual-post-list__header {
  display: flow-root;
}

.virtual-post-list__body {
  margin-top: 12px; /* LIST_GAP_PX */
}

.virtual-item-wrapper {
  will-change: transform;
  contain: layout style paint;
  backface-visibility: hidden;
  transform: translateZ(0);
}

.loading-block,
.error-block,
.empty-state-block {
  padding: 60px 20px;
}

.skeleton-loading-container {
  padding: 0 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.error-block h3 {
  margin: 16px 0 8px;
  color: var(--f7-text-color);
}

.empty-icon-container {
  margin-bottom: 20px;
  opacity: 0.6;
}

.empty-state-block h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--f7-text-color);
}

@media (max-width: 480px) {
  .loading-block,
  .error-block,
  .empty-state-block {
    padding: 40px 16px;
  }
}
</style>
